import { modalManager } from '../UI/modalManager.js';
import { langManager } from '../lang/langManager.js';
import { initTurnstile } from '../utils/turnstile.js';
import { cartAdd, setCategories, initCart } from './pageBoutiqueCart.js';
import '/css/boutique.css';

const LOG = (...args) => console.log('[Boutique]', ...args);
const WARN = (...args) => console.warn('[Boutique]', ...args);

/************************************** */
/* PAGE CONFIGURATION                   */

const scenePageMap = {
  home: '/',
  market: '/market',
  catering: '/catering',
  about: '/about',
  games: '/games',
  boutique: '/boutique'
};

const pageConfig = {
  initialScene: 'boutique',
  navigationMode: 'spa',
  history: true,
  scenePageMap,
  prefetchScenes: ['home', 'market', 'catering', 'about', 'games']
};

if (!window.__BUBAR_PAGE__) {
  window.__BUBAR_PAGE__ = pageConfig;
}
import('../core/page.js').catch(err =>
  console.error('[Boutique] Failed to load page runtime', err)
);

/*********************************************
 * Cabinet Modal Builder
 *********************************************/

// Resolve a string-or-i18n-object using the current language
function t(value) {
  if (!value || typeof value === 'string') return value || '';
  const lang = langManager.getCurrentLanguage?.() || 'de';
  return value[lang] || value['fr'] || value['en'] || Object.values(value)[0] || '';
}

/**
 * Build the cabinet DOM inside a modal element for a given category.
 *
 * DOM structure written here:
 *   .cabinet-wrap-outer        ← clipping boundary (HTML, pre-existing)
 *   └── .cabinet-wrap          ← square, scaled on mobile (HTML, pre-existing)
 *       ├── img.cabinet-img    ← cabinet background image
 *       └── button.product-spot × N
 *           ├── img.product-spot__img
 *           └── div.product-tooltip
 *               └── div.product-tooltip__card
 *
 * Spot positioning: left/top % are relative to .cabinet-wrap, which is
 * always square and matches the cabinet image 1:1. On mobile the whole
 * .cabinet-wrap is scaled via CSS transform — spots move with the image.
 *
 * @param {HTMLElement} modalElement
 * @param {string} categoryId
 */

// Placeholder text per language
const PLACEHOLDER = {
  fr: 'Survolez un produit…',
  de: 'Produkt berühren…',
  en: 'Hover over a product…'
};

function buildCabinetModal(modalElement, categoryId) {
  const category = CATEGORIES[categoryId];
  if (!category) return;

  const titleEl = modalElement.querySelector('.cabinet-title');
  if (titleEl) {
    titleEl.textContent =
      langManager.getText(`boutique${categoryId}Title`) || t(category.name);
  }



  const cabinetWrap = modalElement.querySelector('.cabinet-wrap');
  if (!cabinetWrap) return;
  cabinetWrap.innerHTML = '';

  // image left for boite, right for feinkost/drinks


const panel = modalElement.closest('.modal-panel') || modalElement.querySelector('.modal-panel') || modalElement;
const overlay = panel.closest('.modal-overlay') || modalElement.closest('.modal-overlay');
overlay?.classList.add('has-cabinet-modal');
  // ---- Info panel (injected just before cabinet-wrap-outer) ----
  // const outer = modalElement.querySelector('.cabinet-wrap-outer');

    // Move title inside info-wrap-outer so position:absolute works correctly
const outer = modalElement.querySelector('.cabinet-wrap-outer');
let infoPanel = modalElement.querySelector('.cabinet-info-panel');
if (!infoPanel) {
  infoPanel = document.createElement('div');
  infoPanel.className = 'cabinet-info-panel';
  outer.parentElement.insertBefore(infoPanel, outer);
}
if (titleEl && !infoPanel.contains(titleEl)) {
  infoPanel.prepend(titleEl);
}


let lockedSpot = null;
let currentlyShownProduct = null;

function showPlaceholder() {
  const title = infoPanel.querySelector('.cabinet-title');
  const currentLang = langManager.getCurrentLanguage?.() || 'fr';
  const placeholderText = PLACEHOLDER[currentLang] || PLACEHOLDER.fr;
  infoPanel.innerHTML = `<p class="cabinet-info-panel__placeholder">${placeholderText}</p>`;
  if (title) infoPanel.prepend(title);
  infoPanel.classList.remove('is-visible');
  currentlyShownProduct = null;
}

function attachSwipeDown(panel) {
  let startY = 0;
  let dragging = false;

panel.addEventListener('touchstart', e => {
  if (e.target.closest('button')) return;   // ← add this line
  startY = e.touches[0].clientY;
  dragging = true;
}, { passive: false });

  panel.addEventListener('touchmove', e => {
    if (!dragging) return;
    const delta = e.touches[0].clientY - startY;
    if (delta > 0) {
      panel.style.transform = `translateY(${delta}px)`;
    }
  }, { passive: false });

  panel.addEventListener('touchend', e => {
    if (!dragging) return;
    dragging = false;
    const delta = e.changedTouches[0].clientY - startY;
    if (delta > 80) {
      // dismissed
      panel.style.transform = '';
      panel.classList.remove('is-visible');
      lockedSpot = null;
      cabinetWrap.querySelectorAll('.product-spot--open')
        .forEach(s => s.classList.remove('product-spot--open'));
      showPlaceholder();
    } else {
      // snap back
      panel.style.transform = '';
    }
  }, { passive: true });
}


function showProduct(product) {
  const title = infoPanel.querySelector('.cabinet-title');
  const desc = t(product.description);
  const addLabel = langManager.getText('boutiqueAddToCart') || '+ Panier';
  infoPanel.innerHTML = `
    <p class="cabinet-info-panel__name">${t(product.name)}</p>
    ${desc ? `<p class="cabinet-info-panel__desc">${desc}</p>` : ''}
    <p class="cabinet-info-panel__price">${product.price.toFixed(2)} € / ${product.unit}</p>
    <button class="cabinet-info-panel__add" type="button"
      data-product-id="${product.id}"
      data-category-id="${categoryId}">
      ${addLabel}
    </button>
  `;
  if (title) infoPanel.prepend(title);
  currentlyShownProduct = product;
  infoPanel.classList.add('is-visible');
  attachSwipeDown(infoPanel);

infoPanel.querySelector('.cabinet-info-panel__add')
  ?.addEventListener('touchend', e => {
    e.preventDefault();
    cartAdd(categoryId, product.id);
    const btn = infoPanel.querySelector('.cabinet-info-panel__add');
    if (btn) {
      btn.textContent = '✓';
      setTimeout(() => { btn.textContent = addLabel; }, 900);
    }
  });
  }

  showPlaceholder();

  // ---- Cabinet image ----
  const img = document.createElement('img');
  img.src = category.cabinetImage;
  img.alt = t(category.name);
  img.className = 'cabinet-img';
  img.loading = 'lazy';
  cabinetWrap.appendChild(img);



  // ---- Spots (no tooltips) ----
  category.products.forEach(product => {
    const spot = document.createElement('button');
    spot.className = 'product-spot';
    spot.dataset.productId = product.id;
    spot.dataset.categoryId = categoryId;
    spot.style.left = product.x + '%';
    spot.style.top = product.y + '%';

    const scale = parseFloat(product.imageScale) || 1;
    spot.style.setProperty('--spot-scale', scale);
    spot.setAttribute('aria-label', t(product.name));
    spot.setAttribute('type', 'button');

    const spotImg = document.createElement('img');
    spotImg.src = `epicerie/images/${product.image}`;
    spotImg.alt = t(product.name);
    spotImg.className = 'product-spot__img';
    spot.appendChild(spotImg);

    cabinetWrap.appendChild(spot);

    // Per-spot: Mouse enter (Preview)
    spot.addEventListener('mouseenter', () => {
      // If a product is already locked by a click, don't let hovers change the description
      if (lockedSpot) return; 
      
      showProduct(product);
    });

    // Per-spot: Mouse leave (Reset preview)
    spot.addEventListener('mouseleave', () => {
      // If something is locked, don't clear it on mouse leave
      if (lockedSpot) return; 
      
      showPlaceholder();
    });

    // Per-spot: Click (Lock Selection)
    spot.addEventListener('click', e => {
      e.stopPropagation();
      
      // Remove open class from previous spots
      cabinetWrap.querySelectorAll('.product-spot--open')
        .forEach(s => s.classList.remove('product-spot--open'));
      
      // Lock this spot
      lockedSpot = spot;
      spot.classList.add('product-spot--open');
      showProduct(product);
    });
  });

  // Delegated on cabinetWrap — Click outside to unlock and reset
  cabinetWrap.addEventListener('click', e => {
    if (!e.target.closest('.product-spot')) {
      lockedSpot = null; // Clear lock
      cabinetWrap.querySelectorAll('.product-spot--open')
        .forEach(s => s.classList.remove('product-spot--open'));
      showPlaceholder();
    }
  });

// Delegated on cabinetWrap — Hovering over alternative spots while unlocked
  cabinetWrap.addEventListener('mouseover', e => {
    if (lockedSpot) return; // Ignore hover state updates if an item is locked
    
    const hoveredSpot = e.target.closest('.product-spot');
    if (!hoveredSpot) return;
    
    cabinetWrap.querySelectorAll('.product-spot--open')
      .forEach(s => { if (s !== hoveredSpot) s.classList.remove('product-spot--open'); });
  });

  // Lang change: refresh info panel content live
  const onLangChange = () => {
    const titleEl = infoPanel.querySelector('.cabinet-title');
    if (titleEl) {
      titleEl.textContent =
        langManager.getText(`boutique${categoryId}Title`) || t(category.name);
    }
    if (currentlyShownProduct) {
      showProduct(currentlyShownProduct);
    } else {
      showPlaceholder();
    }
  };
  window.addEventListener('languageChanged', onLangChange);
}



/*********************************************
 * Wiring up category buttons to open modals
 *********************************************/

function bindCategoryButtons() {
  const map = {
    'open-boite': () => modalManager.openModal('boutique', 'boite-section'),
    'open-feinkost': () => modalManager.openModal('boutique', 'feinkost-section'),
    'open-drinks': () => modalManager.openModal('boutique', 'drinks-section')
  };
  for (const [id, handler] of Object.entries(map)) {
    document.getElementById(id)?.addEventListener('click', handler);
  }
}

/*********************************************
 * Init
 *********************************************/

let CATEGORIES = {};
let categoriesReady = false;
let categoriesResolve;
const categoriesPromise = new Promise(res => {
  categoriesResolve = res;
});

/*********************************************
 * Turnstile — namespaced callback
 * data-callback="onBoutiqueTurnstileSuccess" in the HTML widget div.
 * Namespaced (not onTurnstileSuccess) so it doesn't collide with
 * catering's callback when both pages are in the SPA DOM.
 *********************************************/
window.onBoutiqueTurnstileSuccess = function (token) {
  const field = document.getElementById('boutique-turnstile-token');
  if (field) field.value = token;
};

function init() {
  LOG('init');
  initCart();
  bindCategoryButtons();

  // initTurnstile handles manual render for cases where the Cloudflare
  // script has already run before the cart HTML was injected (SPA timing).
  // The widget div has data-sitekey so auto-render also works if the
  // script loads after injection.
  initTurnstile({
    containerId: 'boutique-turnstile-container',
    tokenFieldId: 'boutique-turnstile-token',
    theme: 'light',
    callback: 'onBoutiqueTurnstileSuccess'
  });
}

async function loadAndInit() {
  try {
    const res = await fetch('/epicerie/data/products.json');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    CATEGORIES = await res.json();
    setCategories(CATEGORIES);
    LOG('CATEGORIES loaded', Object.keys(CATEGORIES));
  } catch (err) {
    console.error('[Boutique] Failed to load products.json', err);
  }

  categoriesReady = true;
  categoriesResolve();

  ['boite', 'feinkost', 'drinks'].forEach(categoryId => {
    modalManager.registerModal('boutique', `${categoryId}-section`, {
      initialize: async el => {
        await categoriesPromise;
        buildCabinetModal(el, categoryId);
      },
      updateContent: el => {
        if (!categoriesReady) return;
        const wrap = el.querySelector('.cabinet-wrap');
        if (wrap && wrap.children.length > 0) return;
        buildCabinetModal(el, categoryId);
      }
    });
  });

  if (modalManager.isSceneReady?.('boutique')) {
    LOG('modals-ready already fired — initializing now');
    await modalManager.initializeSceneModals?.('boutique');
  }
}

let cartInitialized = false;

window.addEventListener('modals-ready', e => {
  if (e.detail?.sceneName === 'boutique' && !cartInitialized) {
    LOG('modals-ready: binding cart UI');
    cartInitialized = true;
    init();
  }
});

loadAndInit();

/*********************************************
 * Public API
 *********************************************/

export function openBoiteCrepes() {
  LOG('openBoiteCrepes');
  return modalManager.openModal('boutique', 'boite-section');
}

export function openFeinkost() {
  LOG('openFeinkost');
  return modalManager.openModal('boutique', 'feinkost-section');
}

export function openDrinks() {
  LOG('openDrinks');
  return modalManager.openModal('boutique', 'drinks-section');
}